import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Kpi, Loading, ErrorBox } from "@/components/Widgets";
import { API, useApi, nf, errMsg } from "@/lib/api";

const RANGES = [[30, "30 giorni"], [90, "90 giorni"], [180, "6 mesi"], [365, "1 anno"]];

const delta = (rows, k) => {
  if (rows.length < 2) return null;
  const d = rows[rows.length - 1][k] - rows[0][k];
  return `${d >= 0 ? "+" : ""}${nf(Math.round(d * 100) / 100)} nel periodo`;
};

const Chart = ({ rows, lines, title, testid }) => (
  <Panel title={title} testid={testid}>
    <div className="h-64"><ResponsiveContainer>
      <LineChart data={rows}>
        <CartesianGrid stroke="#F1F1EC" vertical={false} />
        <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tickLine={false} axisLine={false} minTickGap={20} />
        <YAxis tickLine={false} axisLine={false} width={50} tickFormatter={nf} />
        <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map(([k, n, c]) => <Line key={k} type="monotone" dataKey={k} name={n} stroke={c} strokeWidth={2} dot={rows.length < 15} />)}
      </LineChart>
    </ResponsiveContainer></div>
  </Panel>
);

export default function History() {
  const [days, setDays] = useState(90);
  const { data, error, loading, reload } = useApi("/history");
  const changeRange = (d) => { setDays(d); reload({ days: d }); };
  const [snapping, setSnapping] = useState(false);
  const snap = async () => {
    setSnapping(true);
    try {
      await axios.post(`${API}/history/snapshot`);
      toast.success("Snapshot salvato");
      reload({ days });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSnapping(false);
    }
  };
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  const rows = data.rows;
  const last = rows[rows.length - 1] || {};
  return (
    <>
      <PageHeader eyebrow={`Snapshot automatico ogni giorno alle 06:00 · ${rows.length} giorni registrati`} title="Storico crescita" desc="Ogni giorno l'app salva follower, copertura, interazioni ed engagement: così vedi come crescono i tuoi canali mese dopo mese.">
        <div className="flex border border-[#E8E8E4] bg-white">{RANGES.map(([d, l]) => <button key={d} data-testid={`range-${d}`} onClick={() => changeRange(d)} className={`px-3 py-2 text-xs transition-colors ${days === d ? "bg-[#2A4B3C] text-white" : "hover:bg-[#F7F7F5]"}`}>{l}</button>)}</div>
        <Button data-testid="snapshot-now-btn" variant="outline" className="rounded-full" onClick={snap} disabled={snapping}>{snapping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}Salva snapshot ora</Button>
      </PageHeader>
      {data.mode === "demo" && <p className="text-xs text-[#C05C3D] mb-4" data-testid="history-demo-note">Modalità demo: storico simulato.</p>}
      {rows.length === 0 ? <Panel><p className="text-sm text-[#5C5C58]" data-testid="history-empty">Nessuno snapshot ancora. Il primo verrà salvato al prossimo aggiornamento dati o alle 06:00.</p></Panel> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Kpi testid="hist-ig-followers" label="Follower IG" value={nf(last.ig_followers)} hint={delta(rows, "ig_followers")} />
            <Kpi testid="hist-fb-followers" label="Follower FB" value={nf(last.fb_followers)} hint={delta(rows, "fb_followers")} />
            <Kpi testid="hist-er" label="Engagement medio" value={`${last.avg_er}%`} hint={delta(rows, "avg_er")} accent />
            <Kpi testid="hist-reach" label="Copertura 30gg" value={nf(last.ig_reach_30d)} hint={delta(rows, "ig_reach_30d")} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Chart testid="hist-followers-chart" title="Follower" rows={rows} lines={[["ig_followers", "Instagram", "#2A4B3C"], ["fb_followers", "Facebook", "#C05C3D"]]} />
            <Chart testid="hist-er-chart" title="Engagement rate medio (%)" rows={rows} lines={[["avg_er", "ER %", "#C9A227"]]} />
            <Chart testid="hist-reach-chart" title="Copertura e interazioni (30gg mobili)" rows={rows} lines={[["ig_reach_30d", "Copertura", "#2A4B3C"], ["interactions_30d", "Interazioni", "#7A8B4F"]]} />
            <Chart testid="hist-engagement-chart" title="Like e commenti medi per post" rows={rows} lines={[["avg_likes", "Like medi", "#C05C3D"], ["avg_comments", "Commenti medi", "#5E7C8A"]]} />
          </div>
        </>
      )}
    </>
  );
}
